import { useRef, useState } from "react";
import { toast } from "sonner";
import { Mic, Loader2, CheckCircle2, Upload, Square } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function VoiceUploader({ compact = false }) {
  const { user, checkAuth } = useAuth();
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [secs, setSecs] = useState(0);
  const ref = useRef(null);
  const recRef = useRef(null);
  const chunks = useRef([]);
  const tick = useRef(null);
  const hasVoice = !!user?.voice_path;

  const uploadBlob = async (blob, name) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, name);
      await api.post("/voice", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await checkAuth();
      toast.success("Tu voz está lista para clonar");
    } catch {
      toast.error("No se pudo guardar la voz");
    } finally { setBusy(false); }
  };

  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (f) await uploadBlob(f, f.name);
    e.target.value = "";
  };

  const stopRec = () => {
    try { if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop(); } catch { /* noop */ }
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunks.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunks.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        clearInterval(tick.current);
        setRecording(false);
        setSecs(0);
        const blob = new Blob(chunks.current, { type: "audio/webm" });
        if (blob.size > 0) await uploadBlob(blob, "grabacion.webm");
      };
      recRef.current = mr;
      mr.start();
      setRecording(true);
      setSecs(0);
      tick.current = setInterval(() => setSecs((s) => {
        const n = s + 1;
        if (n >= 15) stopRec();
        return n;
      }), 1000);
    } catch {
      toast.error("No se pudo acceder al micrófono. Da permiso e inténtalo de nuevo.");
    }
  };

  return (
    <div className={`mfp-elev rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${compact ? "" : "mt-3"}`} data-testid="voice-uploader">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${hasVoice ? "bg-emerald-500/15" : recording ? "bg-[#FF334B]/20 animate-pulse" : "bg-[#FF334B]/12"}`}>
        {hasVoice ? <CheckCircle2 className="w-5 h-5 text-emerald-400" /> : <Mic className={`w-5 h-5 ${recording ? "text-[#FF334B]" : "text-[#FF334B]"}`} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-head font-semibold text-sm">
          {recording ? `Grabando… ${secs}s / 15s` : hasVoice ? "Tu voz está lista" : "Graba o sube tu voz"}
        </div>
        <div className="text-xs text-[#64748B]">
          {recording ? "Habla con claridad. Se detiene sola a los 15s." : hasVoice ? "La IA usará tu voz para cantar o narrar." : "Graba un trozo corto (~10s) o sube un archivo para clonar tu voz."}
        </div>
      </div>
      <input ref={ref} type="file" accept="audio/*" onChange={pick} className="hidden" data-testid="voice-file-input" />
      <div className="flex items-center gap-2 shrink-0">
        {recording ? (
          <button data-testid="voice-stop-button" onClick={stopRec}
            className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-full bg-[#FF334B] text-white hover:bg-[#e02a40] transition-colors">
            <Square className="w-4 h-4" /> Detener
          </button>
        ) : (
          <button data-testid="voice-record-button" onClick={startRec} disabled={busy}
            className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-full border border-[#FF334B]/50 text-[#FF334B] hover:bg-[#FF334B]/10 transition-colors disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 spin" /> : <Mic className="w-4 h-4" />} Grabar
          </button>
        )}
        <button data-testid="voice-upload-button" onClick={() => ref.current?.click()} disabled={busy || recording}
          className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-full border border-white/12 hover:border-[#FF334B] hover:text-[#FF334B] transition-colors disabled:opacity-50">
          <Upload className="w-4 h-4" /> {hasVoice ? "Cambiar" : "Subir"}
        </button>
      </div>
    </div>
  );
}
